import React from 'react'
import { useRouteMatch, useHistory } from 'react-router-dom'
import cn from 'classnames'
import { values } from 'lodash'

import OrderInformation from './OrderInformation'
import TabOrderTotals from '../TabOrderTotals'
import TabOrderItems from '../TabOrderItems'
import SidebarModal from '../../SidebarModal'
import Button from '../../Button'

import { DEFAULT_EXEMPTIONS } from '../../../utils/orderTotalUtils'
import OrderState from '../../../utils/orderStates'
import useManagerApproval from '../../../hooks/useManagerApproval'
import confirmationNotification from '../../../hooks/useConfirmationNotification'

import styles from './Details.module.scss'
import useUpdateUserNotes from '../../../hooks/useUpdateUserNotes'
import VNNotesField from '../../VNNotesField'
import PaymentTypes from '../../../utils/paymentTypes'

import { ReactComponent as ErrorIcon } from '../../../assets/icons/red-exclamation.svg'

const Details = ({ orderId, customerId, revenueCenterId, orderState, orderNumber, userNotes, modifiedAt, groupedItems, itemCount, isRichCheckout, subtotal, total, tip, exemptions, suiteInformation, payment, paymentType, remoteOrderTotal, isOrderLatestFromServer, exemptionOverrides, onReopenOrder, onAddServiceRound, onClose, frozen, isSuitesMode, authorizationFailed, isMultiPayment, remainingBalance, remainingBalanceInCents, errorMessage, syncStatus, className, signatureRequired, isNoTipMenu }) => {
  const classNames = cn(styles.details, className)
  const history = useHistory()
  const { url } = useRouteMatch()
  const [approvalModal, requestApproval] = useManagerApproval()
  const [updateUserNotes, userNotesStatus] = useUpdateUserNotes()

  const unsynced = syncStatus !== 'synced'

  const handleCloseOrder = () => {
    let path = isMultiPayment ? `/tender/${orderId}` : `${url}/close`
    history.push(path)
  }

  const handleViewReceipt = () => {
    const path = isMultiPayment ? `/tabs/${orderId}/receiptSelect` : `/receipt/${orderId}`
    history.replace(path)
  }

  let buttons = [
    !authorizationFailed && <Button disabled={!isOrderLatestFromServer} key='add' onClick={() => onAddServiceRound(customerId, revenueCenterId)} className={styles.actionButton}>Add a Service Round</Button>,
    <Button disabled={!isOrderLatestFromServer} key='close' onClick={handleCloseOrder} className={styles.actionButton}>Close Order</Button>,
    !authorizationFailed && <Button disabled={!isOrderLatestFromServer} key='more' onClick={() => history?.push?.(`${url}/more`)} className={styles.actionButton}>More Options</Button>,
    authorizationFailed && !isSuitesMode ? <Button disabled={!isOrderLatestFromServer} key='void' onClick={() => history?.replace?.(`/tabs/${orderId}/void`)} className={styles.actionButton}>Void Items</Button> : null,
  ]

  if (payment.toLowerCase() === 'mobile pay' || PaymentTypes.isMobileWalletPayment(paymentType) || unsynced) {
    buttons = []
  }

  const cascadingExemptionOverrides = () => {
    // honor what's returned from stadium; no need to finagle anything
    if (isOrderLatestFromServer) return DEFAULT_EXEMPTIONS

    // placed on this device, but there was an error fetching. use overrides stored when placing order
    if (exemptionOverrides) return exemptionOverrides

    // not sure; return the values from ordering preference
    return exemptions
  }

  const handleReopenOrder = () => {
    confirmationNotification(
      () =>
        requestApproval(() => {
          onReopenOrder(orderId)
          onClose()
        }),
      frozen
    )
  }
  if (OrderState.isClosed(orderState)) {
    buttons = [
      <Button disabled={!isOrderLatestFromServer} key='receipt' onClick={handleViewReceipt} className={styles.actionButton}>View Receipt</Button>,
      (orderState === 'completion_pending' || orderState === 'settlement_failed') && !isRichCheckout ? <Button disabled={!isOrderLatestFromServer} key='reopen' onClick={handleReopenOrder} className={styles.actionButton}>Re-open Order</Button> : null,
      (orderState === 'completion_pending' && !isSuitesMode) || isRichCheckout ? <Button disabled={!isOrderLatestFromServer} key='void' onClick={() => history?.replace?.(`/tabs/${orderId}/void`)} className={styles.actionButton}>Void Items</Button> : null,
    ]
  }

  const authorizationFailedMessage = authorizationFailed && (
    <div className={styles.authorizationFailedMessage}>This order is in an authorization failed state. Payment must be collected before updating or closing the order.</div>
  )

  const orderFailedMessage = unsynced && (
    <div className={styles.orderFailed}><ErrorIcon className={styles.errorIcon} /><div className={styles.errorMessage}>{errorMessage}</div></div>
  )

  return (
    <SidebarModal
      show
      title="Order Details"
      onClose={onClose}
      className={classNames}
    >
      <div className={styles.bodyContainer}>
        <OrderInformation account={suiteInformation?.displayName} payment={isMultiPayment ? 'Split' : payment} amount={remoteOrderTotal?.subtotalDisplay ?? subtotal} modifiedAt={modifiedAt} orderNumber={orderNumber} note={userNotes} remainingBalance={remainingBalanceInCents > 0 && remainingBalance}/>
        <TabOrderItems className={styles.itemsContainer} items={groupedItems} />
        <TabOrderTotals
          className={styles.tabOrderTotals}
          discount={remoteOrderTotal?.discountDisplay}
          exemptions={cascadingExemptionOverrides()}
          nonExemptableTaxRules={values(remoteOrderTotal?.nonExemptableTaxRules ?? [])}
          serviceCharge={remoteOrderTotal?.serviceChargeDisplay}
          serviceChargeName={remoteOrderTotal?.serviceChargeName}
          subtotal={remoteOrderTotal?.subtotalDisplay ?? subtotal}
          tax={remoteOrderTotal?.taxDisplay ?? '-'}
          tip={tip}
          total={total}
        />
        <VNNotesField previousNotes={userNotes} onBlur={notes => updateUserNotes(orderId, notes)} />
        <div className={cn([styles.buttonContainer, unsynced && styles.error])}>
          {orderFailedMessage}
          {authorizationFailedMessage}
          {buttons}
        </div>
      </div>
      {approvalModal}
    </SidebarModal>
  )
}

export default Details
